import Container from '@mui/material/Container';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import React from 'react';
import {LinkProps as NavLinkProps, useLocation} from 'react-router-dom';

import {ProductLogo, useMenuList} from 'presentation/components';
import {useHostName} from 'presentation/hooks/useHostName';
import Icon from 'presentation/icons';
import {useHandleNewAppointment, useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';

import {useTheme} from '@mui/material';
import {RefundEnum} from 'presentation/constants';
import {useExitFlowModalStore} from 'presentation/pages/private/refund/pages/Request/store/useExitFlowModalStore';
import {useExitFlowSidebarRouteStore} from 'presentation/pages/private/refund/pages/Request/store/useExitFlowSidebarRouteStore';
import {useSideBarOpenStateStore} from 'presentation/pages/private/store/sideBarOpenStateStore';
import {MenuListType} from '../types';
import {ICON_LOGO_HEIGHT, IS_BOOKING_FLOW_PATHS} from './constants';
import {
	CloseButton,
	OpenButton,
	StyledDrawer,
	StyledList,
	StyledListItemIcon,
	StyledModalButton,
	StyledNavLink,
	StyledToolbar,
} from './styles';

const {booking} = translations['pt-br'].others.sidebar;

export function Sidebar() {
	const {palette} = useTheme();
	const setSideBarOpenState = useSideBarOpenStateStore(state => state.setSideBarOpenState);
	const isSideBarOpenState = useSideBarOpenStateStore(state => state.sideBarOpenState);
	const setExitFlowModalState = useExitFlowModalStore(state => state.setOpen);
	const setExitFlowSidebarRouteState = useExitFlowSidebarRouteStore(state => state.setRoute);
	const {dataHostNameState, isClinipam, isGndiSaoPaulo} = useHostName();
	const {handleNewAppointment} = useHandleNewAppointment();
	const {MENU_LIST} = useMenuList();
	const {pathname} = useLocation();
	const {isMobile} = useMediaQueries();
	const isBookingPath = IS_BOOKING_FLOW_PATHS.some(path => pathname.includes(path));

	const isRequestRefund = pathname === RefundEnum.REQUEST;

	const toggleDrawerOpen = () => {
		setSideBarOpenState(!isSideBarOpenState);
	};

	const Link = React.forwardRef<HTMLAnchorElement, NavLinkProps>((itemProps, ref) => (
		<StyledNavLink ref={ref} open={isSideBarOpenState} {...itemProps} />
	));

	const ModalLink = React.forwardRef<HTMLDivElement>((itemProps, ref) => (
		<StyledModalButton
			ref={ref}
			open={isSideBarOpenState}
			$isBooking={isBookingPath}
			{...itemProps}
		/>
	));

	const handleModalActionLink = (item: MenuListType) => {
		if (isRequestRefund) {
			setExitFlowModalState(true);
			if (item?.route && item?.title !== booking) {
				setExitFlowSidebarRouteState(item.route);
			}
			return;
		}
		if (item.title === booking) {
			handleNewAppointment();
		}
	};

	const customLogoSidebarHeight = () => {
		if (isClinipam) return 43;
		if (isGndiSaoPaulo) return 40;
		return 32;
	};

	return (
		<StyledDrawer variant="permanent" open={isSideBarOpenState}>
			<StyledToolbar open={isSideBarOpenState}>
				{isSideBarOpenState ? (
					<Stack height={80} direction="row" alignItems={'center'}>
						<ProductLogo customHeight={customLogoSidebarHeight()} isMobile={isMobile} />
						<CloseButton onClick={toggleDrawerOpen}>
							<Icon name="chevron-left-outlined" />
						</CloseButton>
					</Stack>
				) : (
					dataHostNameState.productIcon({size: ICON_LOGO_HEIGHT})
				)}
			</StyledToolbar>
			{!isSideBarOpenState ? (
				<OpenButton onClick={toggleDrawerOpen}>
					<Icon name="chevron-right-outlined" size={20} color={palette.common.white} />
				</OpenButton>
			) : null}
			<Container disableGutters={!isSideBarOpenState}>
				<StyledList open={isSideBarOpenState}>
					{MENU_LIST.map((item: MenuListType) => {
						const isFunctionAction = item?.title === booking || isRequestRefund;
						return (
							<ListItemButton
								data-tag-id={item?.dataTagId}
								data-testid={item?.dataTestId}
								key={item?.route}
								component={isFunctionAction ? ModalLink : Link}
								to={isFunctionAction ? undefined : item?.route}
								onClick={
									isFunctionAction ? () => handleModalActionLink(item) : undefined
								}
								id={item?.id}>
								<StyledListItemIcon open={isSideBarOpenState}>
									<Icon name={item?.icon} />
								</StyledListItemIcon>
								{isSideBarOpenState ? (
									<ListItemText
										secondary={item?.title}
										sx={{margin: 0}}
										color="primary"
									/>
								) : null}
							</ListItemButton>
						);
					})}
				</StyledList>
			</Container>
		</StyledDrawer>
	);
}
